import { useState, useEffect, useRef } from 'react';
import { useMediaQuery } from "react-responsive";
import { Box, Stepper, Step, StepLabel, Button, styled, CircularProgress } from '@mui/material';
import { useHistory, Switch, Route } from 'react-router-dom';
import { Container } from '@mui/material';
import { toast, Bounce } from 'react-toastify';
import { api } from '../services/api';
import CrearEvento from '../views/eventos/evento/CrearEvento';
import { SegundoPaso } from '../views/eventos/evento/SegundoPaso';
import { AgregarColaboradores } from '../views/eventos/evento/AgregarColaboradores';
import { PublicarEvento } from '../views/eventos/evento/PublicarEvento';
import { Check } from '@mui/icons-material';

const steps = [
	'Información',
	'Tarifas',
	'Colaboradores',
	'Publicar'
];

// estilos para los iconos del stepper
const CustomStepIcon = styled('div')(({ theme, ownerState }) => ({
	display: 'flex',
	height: 35,
	width: 35,
	borderRadius: '50%',
	justifyContent: 'center',
	alignItems: 'center',
	fontWeight: 600,
	fontSize: 16,
	background: ownerState.completed ? '#5656ff' : 'white',
	border: `2px solid ${ownerState.active || ownerState.completed ? '#5656ff' : '#e0e0e0'}`,
	color: ownerState.completed ? 'white' : '#0b0062',
	zIndex: 1,
}));

function StepIconComponent(props) {
	const { active, completed, className, icon } = props;

	return (
		<CustomStepIcon ownerState={{ active, completed }} className={className}>
			{completed ? <Check fontSize="small" style={{ color: 'white' }} /> : icon}
		</CustomStepIcon>
	);
}

const StyledStepLabel = styled(StepLabel)(() => ({
	'& .MuiStepLabel-label': {
		color: '#0b0062',
		fontFamily: 'Bricolage Grotesque',
		fontSize: "16px",
		marginTop: 8,
		'&.Mui-active': {
			fontWeight: '600',
		},
	},
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
	'& .MuiStepConnector-line': {
		borderTopWidth: 2,
		borderColor: '#e0e0e0',
	},
	'& .MuiStepConnector-active .MuiStepConnector-line': {
		borderColor: '#5656ff',
	},
	'& .MuiStepConnector-completed .MuiStepConnector-line': {
		borderColor: '#5656ff',
	},
}));

export const StepperCrearEvento = () => {
	const history = useHistory();
	const isMobile = useMediaQuery({ query: `(max-width: 770px)` });
	const [eventId, setEventId] = useState(null);
	const [eventoCreado, setEventoCreado] = useState(false);
	const [isPublic, setIsPublic] = useState(false);
	const [stepsToShow, setStepsToShow] = useState(steps.slice(0, 3));
	const [activeStep, setActiveStep] = useState(0);
	const [completedSteps, setCompletedSteps] = useState({});
	const [isFinalizingEvent, setIsFinalizingEvent] = useState(false);

	//ref para acceder al paso 4 - publicar evento
	const publicarEventoRef = useRef();

	// marcar paso como completado
	const completeStep = (step) => {
		setCompletedSteps(prev => ({
			...prev,
			[step]: true
		}));
	};

	const handleNext = async () => {
		const isLastStep = activeStep === stepsToShow.length - 1;

		if (isLastStep) {
			setIsFinalizingEvent(true);

			try {
				// si estamos en el paso de publicacion, primero hacemos la publicacion y las faqs
				if (activeStep === 3 && isPublic && publicarEventoRef.current) {
					const publicationResult = await publicarEventoRef.current.createPublicacionAndFaqs();
					if (!publicationResult.success) {
						throw new Error(publicationResult.error || 'Error al crear la publicación');
					}
				}

				// confirmamos el evento
				const response = await api.eventos.confirmEvento(eventId);
				if (response.status === 'success') {
					toast.success('Tu evento se ha creado exitosamente', {
						position: 'top-center',
						autoClose: 3000,
						theme: 'dark',
						transition: Bounce,
					});

					setTimeout(() => {
						history.push(`/eventos/${eventId}`);
					}, 2000);
				} else {
					throw new Error('Error al confirmar el evento');
				}
			} catch (error) {
				toast.error(`Error: ${error.message}`, {
					position: 'top-center',
					autoClose: 6000,
					theme: 'dark',
					transition: Bounce,
				});
			} finally {
				setIsFinalizingEvent(false);
			}
		} else {
			// sino es, avanzamos
			setActiveStep(prevActiveStep => {
				const nextStep = prevActiveStep + 1;
				completeStep(prevActiveStep);

				// si el evento es privado, no aparece el paso de publicacion
				if (nextStep === 3 && !isPublic) {
					handleNext();
					return prevActiveStep;
				}

				switch (nextStep) {
					case 1:
						history.push('/crearEvento/segundoPaso');
						break;
					case 2:
						history.push('/crearEvento/agregarColaboradores');
						break;
					case 3:
						history.push('/crearEvento/publicarEvento');
						break;
					default:
						break;
				}
				return nextStep;
			});
		}
	};

	//si el evento no se creo correctamente no dejo avanzar
	const canProceed = () => {
		if (activeStep === 0 && !eventoCreado) return false;
		if (isFinalizingEvent) return false;
		return true;
	};

	//cambiar el boton dependiendo el paso
	const getButtonText = () => {
		if (isFinalizingEvent) return 'Finalizando...';
		if (activeStep === stepsToShow.length - 1) return 'Finalizar evento';
		return 'Siguiente';
	};

	const handlePublicoChange = (isPublico) => {
		setIsPublic(isPublico);
	};

	// actualizamos los pasos para mostrar dependiendo si es publico o privado
	useEffect(() => {
		if (eventId) {
			api.eventos.get(eventId)
				.then(response => {
					if (response.status === "success") {
						const eventoData = response.data.evento;
						const esPublico = eventoData.privado === 0 || eventoData.privado === false;
						setIsPublic(esPublico);

						if (esPublico) {
							setStepsToShow(steps);
						} else {
							setStepsToShow(steps.slice(0, 3));
						}
					}
				})
				.catch(error => {
					console.error("Error al obtener datos del evento:", error);
					setStepsToShow(steps);
				});
		}
	}, [eventId]);

	const renderStepper = () => (
		<Box sx={{ width: isMobile ? '100%' : '800px', margin: isMobile ? '0 auto' : '20px auto', my: 4 }}>
			<StyledStepper activeStep={activeStep} alternativeLabel>
				{stepsToShow.map((label, index) => (
					<Step key={label} completed={completedSteps[index]}>
						<StyledStepLabel StepIconComponent={StepIconComponent}>
							{label}
						</StyledStepLabel>
					</Step>
				))}
			</StyledStepper>
		</Box>
	);

	return (
		<>
			<Container className="my-3" maxWidth="lg">
				<div>
					<h3 style={{ color: "#001D67" }}>
						Crear evento &gt; <span style={{ fontWeight: 700 }}>{steps[activeStep]}</span>
					</h3>
				</div>
				<div style={{ textAlign: "center" }}>
					{renderStepper()}
				</div>
			</Container>

			<Switch>
				<Route exact path="/crearEvento/creacion">
					<CrearEvento
						setEventId={setEventId}
						setEventoCreado={setEventoCreado}
						onPublicoChange={handlePublicoChange}
						completeStep={completeStep}
					/>
				</Route>
				<Route exact path="/crearEvento/segundoPaso">
					<SegundoPaso eventId={eventId} isPublic={isPublic} />
				</Route>
				<Route exact path="/crearEvento/agregarColaboradores">
					<AgregarColaboradores eventId={eventId} isPublic={isPublic} />
				</Route>
				<Route exact path="/crearEvento/publicarEvento">
					<PublicarEvento ref={publicarEventoRef} eventId={eventId} isPublic={isPublic} />
				</Route>
			</Switch>

			<Container className="my-3" maxWidth="lg">
				<Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, marginRight: "20px", marginBottom: "20px" }}>
					<Box sx={{ flex: '1 1 auto' }} />

					<Button
						variant="contained"
						onClick={handleNext}
						disabled={!canProceed()}
						sx={{
							backgroundColor: '#5656ff',
							'&:hover': {
								backgroundColor: '#4545ee'
							}
						}}
					>
						{isFinalizingEvent && (
							<CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
						)}
						{getButtonText()}
					</Button>
				</Box>
			</Container>
		</>
	);
};